import { ReactElement } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Description from '@material-ui/icons/Description';
import HelpOutlined from '@material-ui/icons/HelpOutlined';
import NotInterestedOutlined from '@material-ui/icons/NotInterestedOutlined';
import BookmarkBorderOutlined from '@material-ui/icons/BookmarkBorderOutlined';
import LanguageOutlined from '@material-ui/icons/LanguageOutlined';
import { FontFamily } from '@principal-developer-portal/ui';
import { CardItem } from './types/support';

interface ModalSupportCardProps {
  cardItems: CardItem[];
}

// eslint-disable-next-line max-lines-per-function
const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    maxHeight: '592px',
    borderRadius: '4px',
    gap: '16px',
    padding: '16px',
    flex: '0 1 auto',
    // @ts-expect-error custom color
    backgroundColor: `${theme.palette.lightGray}`,
    overflowY: 'auto',
    overflowX: 'hidden',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-track': {
      background: '#E8E8E8',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb': {
      background: '#9D9EA4',
      borderRadius: '50px',
      paddingBottom: '32px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
    boxShadow: 'none',
  },
  heading: {
    width: 'auto',
    minHeight: '32px',
    whiteSpace: 'normal',
    overflow: 'visible',
    fontFamily: FontFamily.Bold,
    fontWeight: 400,
    paddingBottom: '24px',
    fontSize: '20px',
    lineHeight: '32px',
  },
  icon: {
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    paddingTop: '4px',
    paddingBottom: '4px',
    color: `${theme.palette.link}`,
  },
  title: {
    color: `${theme.palette.link}`,
    whiteSpace: 'normal',
    overflow: 'visible',
    fontFamily: FontFamily.Bold,
    fontWeight: 400,
    lineHeight: '24px',
    marginBottom: '4px',
    fontSize: '14px',
    wordBreak: 'break-word',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:focus': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  description: {
    fontFamily: FontFamily.Regular,
    fontSize: '14px',
    lineHeight: '24px',
    whiteSpace: 'normal',
    overflow: 'visible',
    wordBreak: 'break-word',
  },
  cardContent: {
    padding: '0px',
  },
}));

const iconMap = {
  support: <HelpOutlined />,
  documentation: <Description />,
  github: <LanguageOutlined />,
  exception: <NotInterestedOutlined />,
  other: <BookmarkBorderOutlined />,
};

export const ModalSupportCard = (
  props: ModalSupportCardProps,
): ReactElement => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardContent key="modal-support" className={classes.cardContent}>
        <Typography
          variant="h3"
          className={classes.heading}
          data-testid="Support title"
        >
          Resources and support
        </Typography>
        {props.cardItems.map(cardItem => (
          <Grid
            container
            spacing={2}
            key={cardItem.title}
            alignItems="flex-start"
            style={{ paddingBottom: '16px' }}
          >
            <Grid item alignContent="flex-start">
              <div
                className={classes.icon}
                data-testid={`${cardItem.title}-icon`}
              >
                {iconMap[cardItem.type]}
              </div>
            </Grid>
            <Grid item xs>
              <Typography
                className={classes.title}
                data-testid={`${cardItem.title}-link`}
                title={cardItem.title}
              >
                <Link
                  href={cardItem.href}
                  className={classes.title}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cardItem.title}{' '}
                </Link>
              </Typography>
              <Typography
                className={classes.description}
                data-testid={`${cardItem.title}-description`}
                title={cardItem.description}
                component="div"
              >
                {cardItem.description}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </CardContent>
    </Card>
  );
};
